import { useState, useEffect, useContext } from "react";
import { Box, CircularProgress, Card, CardContent, Button } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { toast } from "react-toastify";
import { i18n } from "../../../translate/i18n";
import useVPSNotifications from "../hooks/useVPSNotifications";
import useSMTPConfig from "../hooks/useSMTPConfig";
import useEmailTemplates from "../hooks/useEmailTemplates";
import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";
import { VPS_ALERTS_CHANNEL_ANCHOR_ID, buildChannelValidationMessages } from "./notificationAlerts.helpers";

// D8.9.3 — Lógica (5 estados + effects + handlers) de NotificationAlertsConfig.
export default function useNotificationAlerts(classes) {
  const {
    loading,
    saving,
    error: loadError,
    config,
    sendingTest,
    saveConfig,
    sendTestNotification,
    fetchConfig
  } = useVPSNotifications();

  // Hooks para obtener datos de SMTP, plantillas y WhatsApp
  const { configs: smtpConfigs } = useSMTPConfig();
  const { templates: emailTemplates } = useEmailTemplates();
  const { whatsApps } = useContext(WhatsAppsContext);

  // Estados del formulario
  const [formData, setFormData] = useState({
    enabled: true,
    notificationType: 'none',
    emailEnabled: false,
    smtpConfigId: null,
    emailTemplateId: null,
    adminEmail: '',
    whatsappEnabled: false,
    whatsappId: null,
    whatsappMessageTemplate: i18n.t("settings.vpsAlerts.defaultWhatsAppTemplate"),
    adminWhatsapp: '',
    enableDiskAlerts: true,
    enableBinaryLogAlerts: true,
    enableCpuAlerts: true,
    enableMemoryAlerts: true,
    enableCriticalAlerts: true,
    diskAlertThreshold: 75,
    binaryLogAlertThreshold: 30.00,
    cpuAlertThreshold: 80,
    memoryAlertThreshold: 85,
    enableAutoCleanup: true,
    autoCleanupDiskThreshold: 75,
    enableDiskAutoCleanup: false,
    alertFrequency: 12
  });

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState({ title: '', message: '', type: 'info' }); // 'info' | 'success' | 'error' | 'warning'

  // Función helper para convertir valores a boolean
  const toBoolean = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === 'true' || value === '1';
    return Boolean(value);
  };

  // Cargar configuración cuando esté disponible
  useEffect(() => {
    if (config) {
      // Buscar plantilla unificada VPS por defecto
      const defaultVpsTemplate = emailTemplates.find(
        t => t.purpose === 'vps_alert' && t.name && t.name.includes('Completa')
      );
      
      // Plantilla WhatsApp por defecto
      const defaultWhatsAppTemplate = i18n.t("settings.vpsAlerts.defaultWhatsAppTemplate");
      
      // Si no hay plantilla configurada, usar la unificada por defecto
      const emailTemplateId = config.emailTemplateId || (defaultVpsTemplate ? defaultVpsTemplate.id : null);
      
      // Si no hay plantilla WhatsApp configurada, usar la por defecto
      const whatsappTemplate = config.whatsappMessageTemplate || defaultWhatsAppTemplate;

      // notificationType es la fuente de verdad del canal activo.
      // Derivar emailEnabled/whatsappEnabled desde notificationType para evitar
      // desincronía con los flags booleanos almacenados en BD.
      // Sin ningún canal activo es «none» (doc 0434, M9): umbrales y limpieza sin avisos.
      const sinCanal = !toBoolean(config.emailEnabled) && !toBoolean(config.whatsappEnabled);
      const nt = sinCanal ? 'none' : (config.notificationType || 'email');

      setFormData(prev => ({
        ...prev,
        enabled: toBoolean(config.enabled),
        notificationType: nt,
        emailEnabled: nt === 'email' || nt === 'both',
        smtpConfigId: config.smtpConfigId || null,
        emailTemplateId: emailTemplateId,
        adminEmail: config.adminEmail || '',
        whatsappEnabled: nt === 'whatsapp' || nt === 'both',
        whatsappId: config.whatsappId || null,
        whatsappMessageTemplate: whatsappTemplate,
        adminWhatsapp: config.adminWhatsapp || '',
        enableDiskAlerts: config.enableDiskAlerts !== undefined ? toBoolean(config.enableDiskAlerts) : true,
        enableBinaryLogAlerts: config.enableBinaryLogAlerts !== undefined ? toBoolean(config.enableBinaryLogAlerts) : true,
        enableCpuAlerts: config.enableCpuAlerts !== undefined ? toBoolean(config.enableCpuAlerts) : true,
        enableMemoryAlerts: config.enableMemoryAlerts !== undefined ? toBoolean(config.enableMemoryAlerts) : true,
        enableCriticalAlerts: config.enableCriticalAlerts !== undefined ? toBoolean(config.enableCriticalAlerts) : true,
        diskAlertThreshold: config.diskAlertThreshold || 75,
        binaryLogAlertThreshold: config.binaryLogAlertThreshold || 30.00,
        cpuAlertThreshold: config.cpuAlertThreshold || 80,
        memoryAlertThreshold: config.memoryAlertThreshold || 85,
        enableAutoCleanup: toBoolean(config.enableAutoCleanup),
        autoCleanupDiskThreshold: config.autoCleanupDiskThreshold || 75,
        enableDiskAutoCleanup: toBoolean(config.enableDiskAutoCleanup),
        alertFrequency: config.alertFrequency || 12
      }));
    }
  }, [config, emailTemplates]);

  /**
   * Manejar cambios en campos del formulario
   */
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Manejar cambio de tipo de notificación
   */
  const handleNotificationTypeChange = (e) => {
    const type = e.target.value;
    setFormData(prev => ({
      ...prev,
      notificationType: type,
      emailEnabled: type === 'email' || type === 'both',
      whatsappEnabled: type === 'whatsapp' || type === 'both'
    }));
  };

  /**
   * Mostrar modal informativo
   */
  const showInfoModal = (title, message, type = 'info') => {
    setInfoModalContent({ title, message, type });
    setInfoModalOpen(true);
  };

  const activeSmtpConfigs = smtpConfigs.filter(c => c.isActive);
  const connectedWhatsApps = whatsApps.filter(w => w.status === 'CONNECTED');
  const dateLocale =
    i18n.language === "pt" ? "pt-BR" : i18n.language === "en" ? "en-US" : "es-ES";

  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateWhatsAppNumber = (number) => {
    if (!number) return true;
    return /^[+]?[0-9]{10,15}$/.test(number);
  };

  const scrollToNotificationChannelSection = () => {
    requestAnimationFrame(() => {
      const el = document.getElementById(VPS_ALERTS_CHANNEL_ANCHOR_ID);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const showValidationIssuesModal = (title, issues, extraNote = "") => {
    const body =
      `${i18n.t("settings.vpsAlerts.validationModal.bodyIntro")}\n\n• ` +
      issues.join("\n• ") +
      (extraNote ? `\n\n${extraNote}` : "");
    showInfoModal(title, body, "warning");
    scrollToNotificationChannelSection();
  };

  /**
   * Enviar notificación de prueba
   * Permite enviar prueba incluso si el sistema no está habilitado completamente
   */
  const handleSendTestNotification = async () => {
    const channelIssues = buildChannelValidationMessages(formData, validateEmail, validateWhatsAppNumber);
    if (channelIssues.length > 0) {
      let extra = "";
      const t = formData.notificationType || "email";
      if ((t === "email" || t === "both") && activeSmtpConfigs.length === 0) {
        extra = i18n.t("settings.vpsAlerts.notes.noActiveSmtpTest");
      }
      if ((t === "whatsapp" || t === "both") && connectedWhatsApps.length === 0) {
        extra = extra
          ? `${extra}\n\n${i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp")}`
          : i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp");
      }
      showValidationIssuesModal(
        i18n.t("settings.vpsAlerts.validationModal.titleTest"),
        channelIssues,
        extra
      );
      return;
    }

    // Abrir modal de carga
    setTestModalOpen(true);
    setTestResult(null);

    try {
      // Enviar datos del formulario para permitir pruebas sin guardar
      const result = await sendTestNotification({
        smtpConfigId: formData.smtpConfigId,
        adminEmail: formData.adminEmail,
        emailTemplateId: formData.emailTemplateId,
        whatsappId: formData.whatsappId,
        adminWhatsapp: formData.adminWhatsapp,
        whatsappMessageTemplate: formData.whatsappMessageTemplate,
        notificationType: formData.notificationType
      });
      
      if (result.success) {
        const sentMessages = [];
        if (result.sent?.email) sentMessages.push(i18n.t("settings.vpsAlerts.test.lineEmailOk"));
        if (result.sent?.whatsapp) sentMessages.push(i18n.t("settings.vpsAlerts.test.lineWhatsappOk"));
        let footer = i18n.t("settings.vpsAlerts.test.footerEmail");
        if (result.sent?.email && result.sent?.whatsapp) {
          footer = i18n.t("settings.vpsAlerts.test.footerBoth");
        } else if (result.sent?.whatsapp) {
          footer = i18n.t("settings.vpsAlerts.test.footerWhatsapp");
        }
        setTestResult({
          success: true,
          message: `${i18n.t("settings.vpsAlerts.test.successIntro")}\n\n${sentMessages.join("\n")}\n\n${footer}`
        });
      } else {
        setTestResult({
          success: false,
          message: `${i18n.t("settings.vpsAlerts.test.errorIntro")}\n\n${result.errors?.join("\n") || result.message || i18n.t("settings.vpsAlerts.test.unknownError")}`
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: `${i18n.t("settings.vpsAlerts.test.errorIntro")}\n\n${error.message || i18n.t("settings.vpsAlerts.test.unknownError")}`
      });
    }
  };

  /**
   * Guardar configuración
   */
  const handleSave = async () => {
    if (formData.enabled) {
      const channelIssues = buildChannelValidationMessages(formData, validateEmail, validateWhatsAppNumber);
      if (channelIssues.length > 0) {
        let extra = "";
        const t = formData.notificationType || "email";
        if ((t === "email" || t === "both") && activeSmtpConfigs.length === 0) {
          extra = i18n.t("settings.vpsAlerts.notes.noActiveSmtpSave");
        }
        if ((t === "whatsapp" || t === "both") && connectedWhatsApps.length === 0) {
          extra = extra
            ? `${extra}\n\n${i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp")}`
            : i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp");
        }
        showValidationIssuesModal(
          i18n.t("settings.vpsAlerts.validationModal.titleSave"),
          channelIssues,
          extra
        );
        return;
      }
    }

    try {
      // La base solo admite email/whatsapp/both: «none» viaja como email con los dos canales apagados.
      const result = await saveConfig({
        ...formData,
        notificationType: formData.notificationType === 'none' ? 'email' : formData.notificationType
      });
      // saveConfig no lanza en error: devuelve null y suele mostrar toast.error
      if (!result) {
        showInfoModal(
          i18n.t("settings.vpsAlerts.save.notSavedTitle"),
          i18n.t("settings.vpsAlerts.save.notSavedBody"),
          "warning"
        );
        return;
      }
      showInfoModal(
        i18n.t("settings.vpsAlerts.save.successTitle"),
        i18n.t("settings.vpsAlerts.save.successBody"),
        "success"
      );
    } catch (error) {
      console.error("Error guardando configuración:", error);
      showInfoModal(
        i18n.t("settings.vpsAlerts.save.errorTitle"),
        i18n.t("settings.vpsAlerts.save.errorBody", {
          message: error.message || i18n.t("settings.vpsAlerts.test.unknownError")
        }),
        "error"
      );
    }
  };

  if (loading && !config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && loadError && !config) {
    return (
      <div className={classes.container}>
        <Card className={classes.card}>
          <CardContent>
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {i18n.t("settings.vpsAlerts.loadError", { error: loadError })}
            </Alert>
            <Button variant="outlined" color="primary" onClick={() => fetchConfig()}>
              {i18n.t("settings.vpsAlerts.retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return {
    activeSmtpConfigs,
    config,
    connectedWhatsApps,
    dateLocale,
    emailTemplates,
    fetchConfig,
    formData,
    handleChange,
    handleNotificationTypeChange,
    handleSave,
    handleSendTestNotification,
    infoModalContent,
    infoModalOpen,
    loadError,
    loading,
    saveConfig,
    saving,
    scrollToNotificationChannelSection,
    sendTestNotification,
    sendingTest,
    setFormData,
    setInfoModalContent,
    setInfoModalOpen,
    setTestModalOpen,
    setTestResult,
    showInfoModal,
    showValidationIssuesModal,
    smtpConfigs,
    testModalOpen,
    testResult,
    toBoolean,
    validateEmail,
    validateWhatsAppNumber,
    whatsApps,
  };
}
